import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  getQuickBooksIntegrationStatus, 
  initiateQuickBooksOAuth, 
  completeQuickBooksOAuth,
  disconnectQuickBooks,
  IntegrationStatus 
} from '../../api/integrations';
import { toast } from 'sonner';
import LoadingComponent from '../LoadingComponent';
import DisconnectConfirmationModal from './DisconnectConfirmationModal';
import moment from 'moment-timezone';
import { useLoadCommunity } from '../../hooks/authhooks';

interface QuickBooksIntegrationProps {
  communityId: string;
  userId: string;
  onClose?: () => void;
}

const QuickBooksIntegration: React.FC<QuickBooksIntegrationProps> = ({ 
  communityId, 
  userId, 
  onClose 
}) => {
  const [status, setStatus] = useState<IntegrationStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isConnecting, setIsConnecting] = useState(false);
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);
  const isLoadingRef = useRef(false);
  const isConnectingRef = useRef(false);
  const statusUpdateDispatchedRef = useRef(false);
  const lastLoadTimeRef = useRef(0);
  const toastShownRef = useRef(false);
  
  // Get community data to access timezone
  const { primaryCommunity } = useLoadCommunity(communityId);
  const timezone = primaryCommunity?.timezone || 'America/New_York';

  const loadStatus = useCallback(async (skipLoadingState = false) => {
    // Prevent multiple simultaneous loads - debounce within 500ms
    const now = Date.now();
    if (isLoadingRef.current || isConnectingRef.current) {
      console.log('QuickBooksIntegration: Skipping loadStatus - already loading or connecting');
      return;
    }
    
    // Debounce: prevent multiple calls within 500ms
    if (now - lastLoadTimeRef.current < 500) {
      console.log('QuickBooksIntegration: Skipping loadStatus - debounced');
      return;
    }
    lastLoadTimeRef.current = now;

    try {
      isLoadingRef.current = true;
      if (!skipLoadingState) {
        setIsLoading(true);
      }
      const integrationStatus = await getQuickBooksIntegrationStatus(communityId, userId);
      console.log('QuickBooksIntegration: Loaded status', integrationStatus);
      setStatus(integrationStatus);
      
      // Show info message if connected successfully
      if (integrationStatus.connected && integrationStatus.accountName) {
        console.log('QuickBooks connected:', integrationStatus.accountName);
      } else if (integrationStatus.connected && !integrationStatus.accountName) {
        console.warn('QuickBooks is connected but account name is not available yet');
      }
    } catch (error) {
      console.error('Error loading QuickBooks status:', error);
      // Only show error toast if not already shown
      if (!toastShownRef.current) {
        toast.error('Failed to load QuickBooks integration status');
        toastShownRef.current = true;
        setTimeout(() => { toastShownRef.current = false; }, 3000);
      }
      // Set status to disconnected on error
      setStatus({ id: 'quickbooks', connected: false });
    } finally {
      isLoadingRef.current = false;
      if (!skipLoadingState) {
        setIsLoading(false);
      }
    }
  }, [communityId, userId]);

  useEffect(() => {
    // Only load on mount - prevent re-loading on every loadStatus change
    // Use a ref to track if we've loaded initially
    if (!isLoadingRef.current && !isConnectingRef.current) {
      loadStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - only run on mount

  const handleConnect = async () => {
    // Prevent multiple simultaneous connection attempts
    if (isConnectingRef.current || isLoadingRef.current) {
      console.log('QuickBooksIntegration: Connection already in progress');
      return;
    }

    try {
      isConnectingRef.current = true;
      setIsConnecting(true);
      
      // Store redirect URI for consistency - must match exactly between initiate and complete
      const redirectUri = `${window.location.origin}/settings/integrations?type=quickbooks`;
      
      const response = await initiateQuickBooksOAuth(communityId, userId);
      
      // Backend now always returns authUrl when successful
      if (response.authUrl) {
        // Open OAuth flow in new window
        const popup = window.open(
          response.authUrl,
          'QuickBooks OAuth',
          'width=600,height=700,scrollbars=yes,resizable=yes'
        );

        if (!popup) {
          toast.error('Please allow popups to connect QuickBooks');
          setIsConnecting(false);
          return;
        }

        // Track if OAuth was completed to avoid duplicate state updates
        let oauthCompleted = false;
        let errorShown = false; // Track if error was already shown to prevent duplicates
        let intervalId: NodeJS.Timeout | null = null;
        let timeoutId: NodeJS.Timeout | null = null;

        // Cleanup function
        const cleanup = () => {
          if (intervalId) clearInterval(intervalId);
          if (timeoutId) clearTimeout(timeoutId);
          window.removeEventListener('message', messageHandler);
        };

        // Helper function to show error only once
        const showError = (message: string) => {
          if (!errorShown) {
            errorShown = true;
            toast.error(message);
          }
        };

        // Listen for OAuth callback message from popup
        const messageHandler = async (event: MessageEvent) => {
          // Verify origin for security
          if (event.origin !== window.location.origin) {
            return;
          }

          // Handle successful OAuth callback
          if (event.data.type === 'quickbooks-oauth-callback') {
            const { code, realmId } = event.data;
            
            if (code && realmId) {
              try {
                // IMPORTANT: Exchange authorization code immediately - QuickBooks codes expire in ~60 seconds
                // Do not delay this call as it can cause "Failed to exchange authorization code" errors
                console.log('Completing QuickBooks OAuth from popup callback:', { code: code ? 'present' : 'missing', realmId, communityId, userId });
                const startTime = Date.now();
                
                // Pass redirectUri for consistency with initiate request
                // Backend requires exact match between initiate and complete redirect URIs
                await completeQuickBooksOAuth(communityId, userId, code, realmId, redirectUri);
                
                const exchangeTime = Date.now() - startTime;
                console.log(`QuickBooks OAuth code exchanged in ${exchangeTime}ms`);
                
                // Show success toast only once
                if (!toastShownRef.current) {
                  toast.success('QuickBooks connected successfully!');
                  toastShownRef.current = true;
                  setTimeout(() => { toastShownRef.current = false; }, 3000);
                }
                
                // Close popup if still open
                if (popup && !popup.closed) {
                  popup.close();
                }
                
                // Cleanup and mark as completed
                oauthCompleted = true;
                cleanup();
                
                // Wait a moment for backend to process and store tokens
                await new Promise(resolve => setTimeout(resolve, 1000));
                
                // Refresh status without showing loading state (silent refresh)
                await loadStatus(true);
                
                // Notify parent to refresh integration statuses - only once
                if (!statusUpdateDispatchedRef.current) {
                  statusUpdateDispatchedRef.current = true;
                  window.dispatchEvent(new CustomEvent('quickbooksStatusUpdated'));
                  // Reset after a delay to allow future updates
                  setTimeout(() => {
                    statusUpdateDispatchedRef.current = false;
                  }, 2000);
                }
              } catch (error: any) {
                console.error('Error completing QuickBooks OAuth:', error);
                
                // Backend now returns standardized error format: {"error": "message"}
                // Backend returns specific, user-friendly error messages that should be displayed directly
                // Examples:
                // - "Authorization code expired or already used. Please try connecting again."
                // - "Invalid QuickBooks credentials. Please contact support."
                // - "Redirect URI mismatch. Please contact support."
                // - "Network timeout while exchanging authorization code. Please try connecting again."
                const errorMessage = error?.response?.data?.error || error?.message || 'Failed to complete QuickBooks connection';
                
                // Display backend error message directly - it's already user-friendly
                // Only add prefix if it's a generic error
                let userMessage = errorMessage;
                if (!errorMessage.includes('Authorization code') && 
                    !errorMessage.includes('Invalid QuickBooks') && 
                    !errorMessage.includes('Redirect URI') && 
                    !errorMessage.includes('Network') &&
                    !errorMessage.includes('timeout')) {
                  // Generic error - add context
                  userMessage = `QuickBooks connection failed: ${errorMessage}`;
                }
                
                showError(userMessage);
                if (popup && !popup.closed) {
                  popup.close();
                }
                oauthCompleted = true;
                cleanup();
              } finally {
                isConnectingRef.current = false;
                setIsConnecting(false);
              }
            } else {
              console.error('QuickBooks OAuth callback missing code or realmId');
              showError('QuickBooks connection failed: Missing OAuth parameters');
              if (popup && !popup.closed) {
                popup.close();
              }
              oauthCompleted = true;
              cleanup();
              isConnectingRef.current = false;
              setIsConnecting(false);
            }
            return;
          }
          
          // Handle OAuth errors from popup
          if (event.data.type === 'quickbooks-oauth-error') {
            const { error } = event.data;
            console.error('QuickBooks OAuth error from popup:', error);
            showError(`QuickBooks connection failed: ${error || 'Unknown error'}`);
            if (popup && !popup.closed) {
              popup.close();
            }
            oauthCompleted = true;
            cleanup();
            isConnectingRef.current = false;
            setIsConnecting(false);
          }
        };

        window.addEventListener('message', messageHandler);

        // Set a timeout to prevent infinite waiting (5 minutes)
        timeoutId = setTimeout(() => {
          if (!oauthCompleted) {
            if (popup && !popup.closed) {
              console.warn('QuickBooks OAuth popup timeout - closing popup');
              popup.close();
            }
            oauthCompleted = true;
            cleanup();
            isConnectingRef.current = false;
            setIsConnecting(false);
            showError('QuickBooks connection timed out. Please try again.');
          }
        }, 300000);

        // Also listen for popup close as fallback
        // Only show error if popup closed without completing OAuth (not due to error or success)
        intervalId = setInterval(() => {
          if (popup?.closed && !oauthCompleted && !errorShown) {
            // Give a small delay to allow message handler to process first
            setTimeout(() => {
              if (!oauthCompleted && !errorShown) {
            oauthCompleted = true;
            cleanup();
            isConnectingRef.current = false;
            setIsConnecting(false);
                showError('QuickBooks connection was canceled or closed');
              }
            }, 500);
          }
        }, 1000);
      }
    } catch (error: any) {
      console.error('Error connecting QuickBooks:', error);
      // Backend now returns standardized error format: {"error": "message"}
      // Use backend error message if available, otherwise use generic message
      const errorMessage = error?.response?.data?.error || error?.message || 'Failed to initiate QuickBooks connection';
      
      // Provide user-friendly messages for specific errors
      if (errorMessage.includes('Missing redirectUri')) {
        toast.error('Configuration error: Missing redirect URI. Please contact support.');
      } else {
        toast.error(errorMessage);
      }
      isConnectingRef.current = false;
      setIsConnecting(false);
    }
  };

  const handleDisconnect = () => {
    setShowDisconnectModal(true);
  };

  const handleConfirmDisconnect = async () => {
    setShowDisconnectModal(false);
    
    try {
      await disconnectQuickBooks(communityId, userId);
      if (!toastShownRef.current) {
        toast.success('QuickBooks disconnected successfully');
        toastShownRef.current = true;
        setTimeout(() => { toastShownRef.current = false; }, 3000);
      }
      await loadStatus(true);
      // Notify parent to refresh integration statuses - only once
      if (!statusUpdateDispatchedRef.current) {
        statusUpdateDispatchedRef.current = true;
        window.dispatchEvent(new CustomEvent('quickbooksStatusUpdated'));
        setTimeout(() => {
          statusUpdateDispatchedRef.current = false;
        }, 2000);
      }
      // Notify parent to refresh integration statuses
      if (onClose) {
        onClose();
      }
    } catch (error) {
      console.error('Error disconnecting QuickBooks:', error);
      if (!toastShownRef.current) {
        toast.error('Failed to disconnect QuickBooks');
        toastShownRef.current = true;
        setTimeout(() => { toastShownRef.current = false; }, 3000);
      }
    }
  };

  if (isLoading) {
    return <LoadingComponent message="Loading QuickBooks integration..." />;
  }

  return (
    <>
      <DisconnectConfirmationModal
        isOpen={showDisconnectModal}
        integrationName="QuickBooks"
        onClose={() => setShowDisconnectModal(false)}
        onConfirm={handleConfirmDisconnect}
      />
      <div className="w-full p-6">
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">QuickBooks Integration</h2>
          {status?.connected && (
            <button
              onClick={handleDisconnect}
              className="px-4 py-2 text-sm font-medium text-red-600 hover:text-red-700 border border-red-300 rounded-md hover:bg-red-50"
            >
              Disconnect
            </button>
          )}
        </div>

        {status?.connected ? (
          <div className="space-y-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-green-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="text-green-800 font-medium">Connected</span>
              </div>
            </div>

            <div className="text-sm text-gray-600 space-y-2">
              {status.accountName ? (
                <p><strong>Account:</strong> {status.accountName}</p>
              ) : status.accountId ? (
                <p><strong>Account ID:</strong> {status.accountId}</p>
              ) : (
                <p><strong>Status:</strong> Connected to QuickBooks</p>
              )}
              
              {status.lastSyncedAt && (
                <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-200">
                  <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div className="flex-1">
                    <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-0.5">Last Synced</p>
                    <p className="text-sm text-gray-700 font-semibold">{moment.utc(status.lastSyncedAt).tz(timezone).fromNow()}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{moment.utc(status.lastSyncedAt).tz(timezone).format('MMM D, YYYY [at] h:mm A')}</p>
                  </div>
                </div>
              )}
              
              {status.connectedAt && !status.lastSyncedAt && (
                <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-200">
                  <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div className="flex-1">
                    <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-0.5">Connected</p>
                    <p className="text-sm text-gray-700 font-semibold">{moment.utc(status.connectedAt).tz(timezone).fromNow()}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{moment.utc(status.connectedAt).tz(timezone).format('MMM D, YYYY [at] h:mm A')}</p>
                  </div>
                </div>
              )}
              
              {status.error && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-2 mt-2">
                  <p className="text-xs text-yellow-800">
                    <strong>Note:</strong> {status.error}
                  </p>
                </div>
              )}
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="text-sm font-medium text-blue-800 mb-2">Available Features</h3>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• Sync invoices and payments</li>
                <li>• Automatic financial data synchronization</li>
                <li>• View QuickBooks transactions</li>
              </ul>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-gray-600">
              Connect your QuickBooks account to sync invoices, payments, and financial data automatically.
            </p>
            <button
              onClick={handleConnect}
              disabled={isConnecting}
              className="px-6 py-3 bg-primary-200 hover:bg-primary-300 text-white font-medium rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isConnecting ? 'Connecting...' : 'Connect QuickBooks'}
            </button>
          </div>
        )}
      </div>
    </div>
    </>
  );
};

export default QuickBooksIntegration;

